import { BLOG_LOCALES } from './blogSchema';

// Every article resolves to an author doc in the `authors` collection. Older
// articles carry whatever authorId wrote them (an admin uid, a script name),
// and the drafting routine may send none at all, so anything that doesn't
// match a saved author falls back to Fer.
export const DEFAULT_AUTHOR_ID = 'fer-brambi';
// Facts-only articles (news, patches, calendars) are signed by the team.
export const TEAM_AUTHOR_ID = 'equipo-rivals-base';

export const MAX_AUTHOR_SOCIALS = 12;
export const MAX_AUTHOR_BIO = 1500;

const emptyLocalized = () => Object.fromEntries(BLOG_LOCALES.map((locale) => [locale, '']));

export const createEmptyAuthor = (overrides = {}) => {
  const tagline = { ...emptyLocalized(), ...(overrides.tagline || {}) };
  const bio = { ...emptyLocalized(), ...(overrides.bio || {}) };
  return {
    id: overrides.id || '',
    name: overrides.name || '',
    avatarUrl: overrides.avatarUrl || '',
    tagline: Object.fromEntries(BLOG_LOCALES.map((locale) => [locale, String(tagline[locale] || '')])),
    // Plain text for the author's page; a blank line starts a new paragraph.
    bio: Object.fromEntries(BLOG_LOCALES.map((locale) => [locale, String(bio[locale] || '')])),
    socials: Array.isArray(overrides.socials)
      ? overrides.socials
        .map((social) => ({ label: String(social?.label || ''), url: String(social?.url || '') }))
        .slice(0, MAX_AUTHOR_SOCIALS)
      : [],
    createdAt: overrides.createdAt || null,
    updatedAt: overrides.updatedAt || null,
  };
};

// Used until Fer's doc is saved from /admin/authors, so the site never shows
// an article without its author box.
export const DEFAULT_AUTHOR = createEmptyAuthor({
  id: DEFAULT_AUTHOR_ID,
  name: 'Fer Brambi',
  tagline: {
    es: 'Creador de contenido de Marvel Rivals',
    en: 'Marvel Rivals Content Creator',
    pt: 'Criador de conteúdo de Marvel Rivals',
  },
  socials: [
    { label: 'Twitch', url: 'https://www.twitch.tv/ferbrambi_' },
    { label: 'YouTube', url: 'https://www.youtube.com/@ferbrambi' },
    { label: 'TikTok', url: 'https://www.tiktok.com/@ferbrambi_' },
    { label: 'Instagram', url: 'https://www.instagram.com/ferbrambi_/' },
    { label: 'Discord', url: 'https://discord.com/invite/FF4EVGVm4Y' },
  ],
});

export const resolveAuthor = (authorId, authors = []) => (
  authors.find((author) => author.id === authorId)
  || authors.find((author) => author.id === DEFAULT_AUTHOR_ID)
  || DEFAULT_AUTHOR
);

export const isKnownAuthorId = (authorId, authors = []) => authors.some((author) => author.id === authorId);

export const getAuthorInitials = (name = '') => (
  name.trim().split(/\s+/).slice(0, 2).map((part) => part[0] || '').join('').toUpperCase() || '?'
);

// The network is read from the link, not the label, so a label like "Mi
// canal" still gets the YouTube icon and colors.
const NETWORK_HOSTS = [
  ['twitch', /(^|\.)twitch\.tv$/],
  ['youtube', /(^|\.)(youtube\.com|youtu\.be)$/],
  ['tiktok', /(^|\.)tiktok\.com$/],
  ['instagram', /(^|\.)instagram\.com$/],
  ['discord', /(^|\.)(discord\.com|discord\.gg)$/],
  ['x', /(^|\.)(x\.com|twitter\.com)$/],
  ['kick', /(^|\.)kick\.com$/],
  ['facebook', /(^|\.)(facebook\.com|fb\.com)$/],
];

export const getSocialNetwork = (url = '') => {
  try {
    const host = new URL(url).hostname.toLowerCase();
    const match = NETWORK_HOSTS.find(([, pattern]) => pattern.test(host));
    return match ? match[0] : 'web';
  } catch {
    return 'web';
  }
};

export const isValidSocialUrl = (url = '') => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

export const validateAuthorPayload = (author) => {
  const issues = [];
  if (!author.id || !/^[a-z0-9-]{1,80}$/.test(author.id)) issues.push('Author id must be lowercase letters, numbers and dashes.');
  if (!author.name.trim()) issues.push('Name is required.');
  if (author.name.length > 80) issues.push('Name must be 80 characters or less.');
  BLOG_LOCALES.forEach((locale) => {
    if (author.tagline[locale].length > 120) issues.push(`Tagline (${locale}) must be 120 characters or less.`);
    if (author.bio[locale].length > MAX_AUTHOR_BIO) issues.push(`Bio (${locale}) must be ${MAX_AUTHOR_BIO} characters or less.`);
  });
  author.socials.forEach((social, index) => {
    if (!social.label.trim()) issues.push(`Link ${index + 1} needs a name.`);
    if (social.label.length > 40) issues.push(`Link ${index + 1} name must be 40 characters or less.`);
    if (!isValidSocialUrl(social.url)) issues.push(`Link ${index + 1} needs a full URL starting with https://.`);
  });
  if (issues.length > 0) throw new Error(issues.join(' '));
};
